import { Link } from 'react-router-dom';
import { Puzzle, Image, PenLine, Globe, Search, BarChart3, Check } from 'lucide-react';

const features = [
  { title: 'Pagina-builder', desc: 'Sleep componenten naar je pagina. Geen code nodig.', icon: Puzzle },
  { title: 'Album & Media', desc: "Upload foto's, organiseer in albums en deel met bezoekers.", icon: Image },
  { title: 'Blog', desc: 'Schrijf en publiceer artikelen met een ingebouwde editor.', icon: PenLine },
  { title: 'Eigen domein', desc: 'Koppel je eigen domeinnaam. SSL automatisch geconfigureerd.', icon: Globe },
  { title: 'SEO-tools', desc: 'Meta-tags en sitemap automatisch gegenereerd per pagina.', icon: Search },
  { title: 'Analytics', desc: 'Bezoekerscijfers zonder cookies. Privacy-vriendelijk.', icon: BarChart3 },
];

const plans = [
  { name: 'Free', price: '\u{20AC}0', period: '/maand', features: ['1 website', '500 MB opslag', 'Subdomein', 'Basisfuncties'], cta: 'Aanmelden', href: '/register', highlighted: false },
  { name: 'Pro', price: 'Op aanvraag', period: '', features: ['3 websites', '5 GB opslag', 'Eigen domein', 'Blog & albums', 'E-mail support'], cta: 'Neem contact op', href: 'mailto:info@occiput.io', highlighted: true },
  { name: 'Business', price: 'Op aanvraag', period: '', features: ['Onbeperkt', '50 GB opslag', 'Prioriteit support', 'White-label', 'SLA'], cta: 'Neem contact op', href: 'mailto:info@occiput.io', highlighted: false },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <span className="text-xl font-bold text-brand-600">Occiput</span>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900">Inloggen</Link>
            <Link to="/register" className="btn-primary text-sm">Aanmelden</Link>
          </div>
        </div>
      </nav>

      <section className="bg-gradient-to-br from-brand-50 to-white pt-20 pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 bg-brand-100 text-brand-700 text-sm font-medium rounded-full mb-6">Nu in beta</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
            Jouw website. <span className="text-brand-600">Zonder gedoe.</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10">
            Occiput is een all-in-one platform om prachtige websites te bouwen, beheren en publiceren.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn-primary px-8 py-3 text-base">Gratis aanmelden</Link>
            <a href="#features" className="btn-secondary px-8 py-3 text-base">Meer ontdekken</a>
          </div>
        </div>
      </section>

      <section id="features" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-16">Alles wat je nodig hebt</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map(f => (
              <div key={f.title} className="card p-6 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5" />
              </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-16">Simpele prijzen</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {plans.map(plan => (
              <div key={plan.name} className={`card p-8 ${plan.highlighted ? 'ring-2 ring-brand-500 shadow-lg' : ''}`}>
                {plan.highlighted && <span className="inline-block px-3 py-1 bg-brand-600 text-white text-xs font-semibold rounded-full mb-4">Populair</span>}
                <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                <div className="mt-2 mb-6">
                  <span className="text-3xl font-extrabold text-gray-900">{plan.price}</span>
                  {plan.period && <span className="text-gray-500 text-sm ml-1">{plan.period}</span>}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-brand-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {plan.href.startsWith('/') ? (
                  <Link to={plan.href} className={plan.highlighted ? 'btn-primary w-full' : 'btn-secondary w-full'}>{plan.cta}</Link>
                ) : (
                  <a href={plan.href} className={plan.highlighted ? 'btn-primary w-full' : 'btn-secondary w-full'}>{plan.cta}</a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-gray-100 py-12">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-lg font-bold text-brand-600">Occiput</span>
          <p className="text-sm text-gray-500">{'\u00A9'} {new Date().getFullYear()} Occiput. Alle rechten voorbehouden.</p>
        </div>
      </footer>
    </div>
  );
}
